import { lastsWeeksOf } from './subscriptionBuilder'

/**
 * Slim bijvullen (docs/VORMGEVING.md › Protocol en abonnement). Puur; getest in
 * core/rules/refillPlan.test.ts. De server rekent elke doos met dezelfde regel uit.
 *
 * - Elk product heeft een op-datum: de dag dat het op is.
 * - Er komt elke `intervalWeeks` één doos. Daarin alleen wat op raakt vóór de doos erna (+ marge),
 *   hoogstens `maxUnitsPerProduct` per product.
 * - Elke doos haalt `minBoxValue`. Het ritme dat het systeem voorstelt is het kortste waarbij dat
 *   lukt: weinig voorraad thuis, alles vers, en toch een gezonde doos.
 * - Elke doos wordt opnieuw uitgerekend met de echte op-datums, dus afronden stapelt zich niet op.
 */

export type RefillRules = {
  /** Minimumbedrag per bijvuldoos (euro). Onder dit bedrag gaat er nooit een doos. */
  minBoxValue: number
  /** Hoogstens zoveel van één product per doos (vers en dicht). */
  maxUnitsPerProduct: number
  /** Ritmes waaruit gekozen wordt (weken). */
  intervals: readonly number[]
  /** Zoveel dagen eerder dan de op-datum moet het er zijn. */
  bufferDays: number
}

export type RefillItem = {
  productId: string
  price: number
  /** Hoe lang één verpakking meegaat, in weken (al bijgesteld met "sneller/langzamer"). */
  lastsWeeks: number
  /** De dag dat de voorraad thuis op is ("YYYY-MM-DD"). */
  runsOutOn: string
}

export type BoxLine = { productId: string; qty: number; price: number }
export type Box = { date: string; lines: BoxLine[]; total: number }

/** Een cadans is ongeschikt als een doos leeg is, te klein, of meer dan het maximum van iets nodig heeft. */
export type BoxProblem = 'leeg' | 'te-klein' | 'te-veel'

/** Hoeveel weken "sneller" of "langzamer" scheelt. */
export const USAGE_STEP_WEEKS = 2
/** Ondergrens voor hoe lang iets meegaat, ook na "sneller". */
export const MIN_LASTS_WEEKS = 2
/** Zo ver vooruit rekent het systeem om een ritme te beoordelen. */
const HORIZON_WEEKS = 52

// ——— Datums (puur, UTC, "YYYY-MM-DD") ———

const toDay = (iso: string) => Math.round(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86_400_000)
const fromDay = (day: number) => new Date(day * 86_400_000).toISOString().slice(0, 10)
export const addDays = (iso: string, days: number) => fromDay(toDay(iso) + days)
export const daysBetween = (from: string, to: string) => toDay(to) - toDay(from)

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * Hoe lang een product meegaat: de catalogus plus de bijstelling van de klant. `base` is het aantal
 * weken uit `productLastsWeeks()` (core/rules/usage.ts), of nog de oude tekst ("± 3 maanden").
 * `null` = onbekend.
 */
export function lastsWeeksFor(base: number | string | null | undefined, usageWeeks = 0): number | null {
  const weeks = typeof base === 'number' ? base : lastsWeeksOf(base ?? undefined)
  return weeks === null ? null : Math.max(MIN_LASTS_WEEKS, weeks + usageWeeks)
}

type Sim = {
  boxes: (Box & { problem: BoxProblem | null })[]
  /** De op-datums na de laatste doos. */
  runsOut: Map<string, string>
}

/**
 * Rekent `count` dozen vooruit vanaf `firstBox`. `skip` geldt alleen voor de eerste doos ("deze keer
 * niet"), `extras` gaan alleen in de eerste doos mee.
 */
export function simulateBoxes(
  items: readonly RefillItem[],
  opts: { firstBox: string; intervalWeeks: number; count: number; rules: RefillRules; skip?: readonly string[]; extras?: readonly BoxLine[] },
): Sim {
  const { firstBox, intervalWeeks, count, rules } = opts
  const runsOut = new Map(items.map((i) => [i.productId, i.runsOutOn]))
  const boxes: Sim['boxes'] = []
  for (let n = 0; n < count; n++) {
    const date = addDays(firstBox, n * intervalWeeks * 7)
    const deadline = addDays(date, intervalWeeks * 7 + rules.bufferDays)
    const lines: BoxLine[] = []
    let tooMany = false
    for (const item of items) {
      if (n === 0 && opts.skip?.includes(item.productId)) continue
      const out = runsOut.get(item.productId) ?? date
      if (out >= deadline) continue
      const lastsDays = Math.max(1, item.lastsWeeks * 7)
      // Vanaf de op-datum, of vanaf de doos als ze al zonder zit.
      const from = out < date ? date : out
      const needed = Math.max(1, Math.ceil(daysBetween(from, deadline) / lastsDays))
      if (needed > rules.maxUnitsPerProduct) tooMany = true
      const qty = Math.min(needed, rules.maxUnitsPerProduct)
      runsOut.set(item.productId, addDays(from, qty * lastsDays))
      lines.push({ productId: item.productId, qty, price: item.price })
    }
    if (n === 0) lines.push(...(opts.extras ?? []))
    const total = round2(lines.reduce((s, l) => s + l.price * l.qty, 0))
    const problem: BoxProblem | null = !lines.length ? 'leeg' : tooMany ? 'te-veel' : total < rules.minBoxValue ? 'te-klein' : null
    boxes.push({ date, lines, total, problem })
  }
  return { boxes, runsOut }
}

/** De eerste doos: op tijd vóór het eerste product dat op raakt, maar nooit eerder dan `earliest`. */
export function firstBoxDate(items: readonly RefillItem[], rules: Pick<RefillRules, 'bufferDays'>, earliest: string): string {
  if (!items.length) return earliest
  const soonest = items.map((i) => i.runsOutOn).sort()[0]
  const target = addDays(soonest, -rules.bufferDays)
  return target > earliest ? target : earliest
}

/** Haalt dit ritme een jaar lang elke doos? */
export function cadenceWorks(items: readonly RefillItem[], intervalWeeks: number, rules: RefillRules, earliest: string): boolean {
  if (!items.length) return false
  const firstBox = firstBoxDate(items, rules, earliest)
  const count = Math.max(2, Math.ceil(HORIZON_WEEKS / intervalWeeks))
  return simulateBoxes(items, { firstBox, intervalWeeks, count, rules }).boxes.every((b) => b.problem === null)
}

/** Alle ritmes die werken, van kort naar lang. */
export const workingCadences = (items: readonly RefillItem[], rules: RefillRules, earliest: string) =>
  [...rules.intervals].sort((a, b) => a - b).filter((w) => cadenceWorks(items, w, rules, earliest))

/** Het voorstel: het kortste ritme dat werkt, of `null` als geen enkel ritme het minimum haalt. */
export const chooseCadence = (items: readonly RefillItem[], rules: RefillRules, earliest: string): number | null =>
  workingCadences(items, rules, earliest)[0] ?? null

/** De komende dozen in dit ritme (voor de winkel en Mijn omgeving). */
export function planBoxes(
  items: readonly RefillItem[],
  opts: { intervalWeeks: number; rules: RefillRules; earliest: string; count?: number; firstBox?: string; skip?: readonly string[]; extras?: readonly BoxLine[] },
): Box[] {
  const firstBox = opts.firstBox ?? firstBoxDate(items, opts.rules, opts.earliest)
  return simulateBoxes(items, { ...opts, firstBox, count: opts.count ?? 3 }).boxes.map(({ problem: _p, ...b }) => b)
}

/**
 * Een doos onder het minimum (na "deze keer niet"): tot wanneer kan hij op? Tot de dag vóór het
 * eerste product dat nog in de doos zit op raakt (min de marge). Nooit vóór de huidige datum.
 */
export function postponeLimit(items: readonly RefillItem[], box: Box, rules: Pick<RefillRules, 'bufferDays'>): string {
  const inBox = new Set(box.lines.map((l) => l.productId))
  const outs = items.filter((i) => inBox.has(i.productId)).map((i) => i.runsOutOn).sort()
  if (!outs.length) return box.date
  const limit = addDays(outs[0], -rules.bufferDays)
  return limit > box.date ? limit : box.date
}

/** Na een verzonden doos: de nieuwe op-datums (voor de server en de demo). */
export function afterShipment(items: readonly RefillItem[], box: Box): Map<string, string> {
  const out = new Map(items.map((i) => [i.productId, i.runsOutOn]))
  for (const line of box.lines) {
    const item = items.find((i) => i.productId === line.productId)
    if (!item) continue
    const from = item.runsOutOn < box.date ? box.date : item.runsOutOn
    out.set(item.productId, addDays(from, line.qty * Math.max(1, item.lastsWeeks * 7)))
  }
  return out
}

/** "meestal € 190 tot € 220": de bandbreedte van de komende dozen. */
export function boxRange(boxes: readonly Box[]): { min: number; max: number } | null {
  const totals = boxes.filter((b) => b.lines.length).map((b) => b.total)
  return totals.length ? { min: Math.min(...totals), max: Math.max(...totals) } : null
}
